import React from "react";

export default function SeoGuideSection() {
  return (
    <section className="stf-section">
      <div className="stf-wrap">
        <div className="stf-seo-grid">
          <article className="stf-article">
            <div className="stf-kicker">Guide</div>
            <h2>Hiring residential and estate security in Australia: what to know before you book</h2>
            <p>
              Residential security carries the same legal requirement as any other type of security work in Australia. Whether you're a single homeowner covering a holiday property or a body corporate managing a gated estate, the rules around who can work on your property and what they're allowed to do are set by your state, not by the strata manager and not by the security company quoting you.
            </p>
            <p>
              This guide covers what homeowners, landlords and body corporates most often get wrong when hiring residential security, and what to check before you confirm anyone.
            </p>

            <h3 id="licence">Do you legally need a licensed security guard?</h3>
            <p>
              Yes, in every state. Gatehouse duty, mobile patrol and alarm response on residential property all count as licensable security work, whether it's a single house or a full estate.
            </p>
            <table>
              <thead>
                <tr>
                  <th>State</th>
                  <th>License commonly required</th>
                  <th>Regulator</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>New South Wales</td>
                  <td>Security License Class 1A, Security Officer</td>
                  <td>NSW Police SLED</td>
                </tr>
                <tr>
                  <td>Victoria</td>
                  <td>Private Security Individual Operator License, security guard activity</td>
                  <td>Victoria Police Licensing and Regulation Division</td>
                </tr>
                <tr>
                  <td>Queensland</td>
                  <td>Security Provider License Class 1, unarmed security officer</td>
                  <td>Office of Fair Trading</td>
                </tr>
                <tr>
                  <td>Western Australia</td>
                  <td>Security Officer license</td>
                  <td>WA Police Licensing</td>
                </tr>
                <tr>
                  <td>South Australia</td>
                  <td>Security Agents License, general guarding function</td>
                  <td>Consumer &amp; Business Services</td>
                </tr>
              </tbody>
            </table>
            <div className="stf-callout">
              <b>Worth knowing:</b> a guard working on private residential property still needs resident consent to enter individual homes or private lots, their authority covers common property and access points, not the inside of someone's house. Set clear boundaries with your body corporate or committee before the first shift.
            </div>

            <h3 id="how-many">How much cover does your property need?</h3>
            <p>
              There's no single national ratio for residential security. Most estates and homeowners work from the size of the property and how it's used.
            </p>
            <ul>
              <li><strong>Number of access points:</strong> every gate, boom gate and pedestrian entry is its own position to cover.</li>
              <li><strong>Occupancy pattern:</strong> a holiday home sitting empty for months is a different risk to a fully occupied estate.</li>
              <li><strong>Common property:</strong> pools, gyms and shared facilities often need their own patrol checkpoint.</li>
              <li><strong>Time of day:</strong> overnight and early morning are typically when break ins happen.</li>
              <li><strong>History:</strong> a property or estate with recent incidents usually needs a heavier patrol schedule, not a lighter one.</li>
            </ul>
            <p>If your insurer has already set a minimum patrol frequency as a condition of cover, treat that as the floor, not the target.</p>

            <h3 id="cost">What residential &amp; estate security costs</h3>
            <p>
              Rates for licensed residential guards in Australia generally sit between $35 and $50 an hour, depending on the work. The biggest factors are day versus overnight, a single property versus estate wide coverage, and gatehouse duty versus mobile patrol. Overnight and weekend shifts sit at the top of that range.
            </p>
            <p>
              The other variable is who you book through. Agencies typically add a margin on top of the guard's rate for coordination and rostering. On Staffoo, guards set their own rate and you see it on their application, so the number you compare is the number you pay. You can see current averages in the pricing section above.
            </p>

            <h3 id="vs">Gatehouse guard or mobile patrol, which do you need?</h3>
            <p>
              Both are licensed the same way, the difference is how coverage reaches your property.
            </p>
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Gatehouse Guard</th>
                  <th>Mobile Patrol</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Typical work</td>
                  <td>Stays at a fixed entry point checking residents and visitors in and out.</td>
                  <td>Drives a scheduled route through the estate or between several properties.</td>
                </tr>
                <tr>
                  <td>Best for</td>
                  <td>Suits an estate with a single controlled entrance.</td>
                  <td>Suits larger estates, acreage or several separate properties under one owner.</td>
                </tr>
                <tr>
                  <td>Coverage style</td>
                  <td>Continuous at one point.</td>
                  <td>Scheduled rounds with gaps in between.</td>
                </tr>
              </tbody>
            </table>

            <h3 id="checklist">What to check before you confirm a guard</h3>
            <ul>
              <li><strong>License class and expiry:</strong> that it matches the work and is current in your state.</li>
              <li><strong>Relevant experience:</strong> residential and estate work is a different job to retail or event work.</li>
              <li><strong>Reviews from similar properties:</strong> volume matters less than whether the properties looked like yours.</li>
              <li><strong>Availability across the full roster:</strong> weekends and overnight are when most properties are exposed.</li>
              <li><strong>Who's responsible for insurance:</strong> and what happens if a guard can't make a shift.</li>
            </ul>
            <p>
              On Staffoo, license verification happens before a guard can apply, and the license class, experience and review history sit on every application you receive.
            </p>

            <h3 id="direct">Hiring direct instead of through an agency</h3>
            <p>
              Traditional agencies take your brief, roster guards internally and send you an invoice. You rarely know who's coming until the day, and you can't request the same person twice. Hiring direct through a platform flips that: you see who applied, you pick, and you can rebook the guards residents already know and trust. For an estate or body corporate, that continuity usually matters more than the hourly rate.
            </p>
            <p>
              If you're hiring for a different setting, the same process applies to corporate and office security, construction site security and event security.
            </p>

            <div className="stf-recap">
              <div className="stf-kicker">Before you post</div>
              <p>
                Confirm the license class your state requires, size your cover from access points and occupancy pattern rather than a guess, and treat agency quotes as a ceiling. Staffoo shows you each guard's real rate before you confirm anyone.
              </p>
            </div>
          </article>

          {/* Sidebar Cards Column */}
          <aside className="stf-seo-aside">
            <div className="stf-toc">
              <h4>On this page</h4>
              <a href="#license">Do you need a licensed guard?</a>
              <a href="#how-many">How much cover you need</a>
              <a href="#cost">What it costs</a>
              <a href="#vs">Gatehouse vs mobile patrol</a>
              <a href="#checklist">Pre booking checklist</a>
              <a href="#direct">Direct vs agency</a>
            </div>

            <div className="stf-side-cta">
              <h4>Know what you need?</h4>
              <p>Post the job free and start receiving applications from licensed residential security guards near you.</p>
              <a href="#" className="stf-btn stf-btn-solid stf-btn-block">Post a residential security job</a>
            </div>

            <div className="stf-aside-module">
              <h4>Related guides</h4>
              <a href="#">Licensing explained by state</a>
              <a href="#">How pricing works</a>
              <a href="#">Hiring for a business or agency</a>
              <a href="#">Construction site security</a>
            </div>

            <div className="stf-aside-trust">
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Every applicant's license is checked before you see their profile.
              </div>
              <div className="stf-aside-trust-row">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#0A7C6E" strokeWidth="2">
                  <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"></path>
                </svg>
                Free to post, no lock in contract, pay after sign off.
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
